import { supabase } from '../../api/supabase'
import { resolveJobPartyAddress, type JobPartyRole } from '../../lib/jobPartyAddress'
import { getCompanySettings, type CompanySettings } from '../../lib/companySettings'
import { resolveBoxtopEmployeeName } from '../../lib/transportNote'
import { docFlagsOf, pickParty } from '../../../supabase/functions/_shared/partyScope.ts'

export interface InvoicePDFContainer {
  container_no: string | null
  seal_no: string | null
  container_type: string | null
  said_to_contain: string | null
}

export interface InvoicePDFLine {
  description: string
  remarks: string | null
  taxable: boolean
  amount: number
}

export interface InvoicePDFData {
  company: CompanySettings
  invoice_no: string
  // Set only for credit notes: the invoice number being credited (printed under the title).
  credit_note_of_invoice_no: string | null
  is_credit_note: boolean
  // 'IMPORT' | 'DUTY_VAT' (invoices.invoice_category) -- null only for rows made before the
  // column was populated; drives the PDF title and the Duty & VAT banner.
  invoice_category: string | null
  invoice_date: string
  due_date: string | null
  currency: string
  vat_number: string | null
  prepared_by: string | null

  customer_account_number: string | null
  billing_name: string | null
  billing_address_lines: string[]

  job_ref: string | null
  your_reference: string | null

  shipper_name: string | null
  shipper_address_lines: string[]
  consignee_name: string | null
  consignee_address_lines: string[]

  origin: string | null
  destination: string | null
  final_destination: string | null
  arrival_date: string | null
  master_no: string | null
  vessel: string | null
  containers: InvoicePDFContainer[]
  pieces: number | null
  cube: number | null
  weight_chargeable: number | null
  weight_actual: number | null

  lines: InvoicePDFLine[]
  invoice_notes: string | null
  taxable_subtotal: number
  non_taxable_subtotal: number
  net: number
  vat_amount: number
  vat_rate_pct: number
  gross: number
}

// Splits a customer's address into display lines for the PDF (one PDF line per element). Z91: the free-text
// customers.address column is retired -- typed customer_addresses win (Invoice > Registered > Operations,
// default first), then the flat structured customer columns for customers that have no typed row.
interface PdfAddressRow { address_type: string; is_default: boolean | null; roles?: string[] | null; primary_roles?: string[] | null; address_lines: string[] | null; city: string | null; postcode: string | null; country: string | null }
type PdfCustomer = { address_line_1?: string | null; address_line_2?: string | null; address_line_3?: string | null; city?: string | null; postcode?: string | null; country?: string | null; customer_addresses?: PdfAddressRow[] | null }
const PDF_ADDRESS_ORDER = ['invoice', 'registered', 'operations']
function addressLines(c: PdfCustomer | null | undefined): string[] {
  if (!c) return []
  const typed = c.customer_addresses ?? []
  for (const type of PDF_ADDRESS_ORDER) {
    // An address can serve several roles now: prefer the one that is primary for this role, then any that carries the role.
    const rows = typed.filter(a => (a.roles ?? [a.address_type]).includes(type))
    const row = rows.find(a => (a.primary_roles ?? (a.is_default ? [a.address_type] : [])).includes(type)) ?? rows[0]
    if (row) {
      const lines = [...(row.address_lines ?? []), row.city, row.postcode, row.country].filter((l): l is string => !!l?.trim())
      if (lines.length > 0) return lines
    }
  }
  return [c.address_line_1, c.address_line_2, c.address_line_3, c.city, c.postcode, c.country].filter((l): l is string => !!l?.trim())
}

// For a job's party the address is the one RECORDED on the job (same database function the job screen and the BoxTop sync read), so
// the invoice can never print an address different from the screen. Falls back to the company-level rule above only if that fails.
async function jobPartyLines(jobId: string, role: JobPartyRole): Promise<string[] | null> {
  try {
    const r = await resolveJobPartyAddress(jobId, role)
    if (!r || r.source === 'none') return null
    const lines = [...(r.address_lines ?? []), r.city, r.postcode, r.country].filter((l): l is string => !!l?.trim())
    return lines.length > 0 ? lines : null
  } catch {
    return null
  }
}

// Assembles everything InvoicePDF.tsx needs from an invoice id. Pulls shipment details (shipper/
// consignee/vessel/container/goods) from the linked job the same way the rest of the app already
// does (Jobs.tsx's JOBS_SELECT / job_goods aggregation) -- doesn't invent a second field-mapping.
// Shipper / Consignee: the party SET (HBL or plain) is decided by pickParty (_shared/partyScope.ts), the same rule the declaration and the
// BoxTop sync use. (An older note here said the HBL override was not applied: that was fixed in 436b887.)
// What the invoice prints for a party: the company row + its address book, fetched for the party the job actually declares.
const PARTY_SELECT = 'id, name, address_line_1, address_line_2, address_line_3, city, postcode, country, customer_addresses(address_type, is_default, roles, primary_roles, address_lines, city, postcode, country)'

export async function fetchInvoicePDFData(invoiceId: string): Promise<InvoicePDFData> {
  const company = await getCompanySettings()

  const { data: invoice, error: invErr } = await supabase
    .from('invoices')
    .select('invoice_no, invoice_type, credit_note_of_invoice_id, invoice_category, invoice_date, due_date, currency, amount, vat_amount, total_amount, created_by, job_id, customer_id, notes')
    .eq('id', invoiceId)
    .single()
  if (invErr || !invoice) throw invErr || new Error('Invoice not found')

  // Same user_roles.boxtop_owner_name lookup as the Delivery/Collection Note's "Issued by"
  // line (see transportNote.ts) -- invoice.created_by is a bare email, not a display name.
  const prepared_by = invoice.created_by ? await resolveBoxtopEmployeeName(invoice.created_by) : null

  const { data: customer } = await supabase
    .from('customers')
    .select('code, name, address_line_1, address_line_2, address_line_3, city, postcode, country, customer_addresses(address_type, is_default, roles, primary_roles, address_lines, city, postcode, country)')
    .eq('id', invoice.customer_id)
    .maybeSingle()

  const isCreditNote = invoice.invoice_type === 'CREDIT_NOTE'
  let creditedInvoiceNo: string | null = null
  let lines: InvoicePDFLine[]
  if (isCreditNote) {
    // A credit note carries its own positive lines (job_charges never go negative).
    const { data: cnLines } = await supabase.from('credit_note_lines').select('description, remarks, amount, vat_rate').eq('credit_note_id', invoiceId)
    lines = (cnLines || []).map((l: any) => ({ description: l.description, remarks: l.remarks || null, taxable: (l.vat_rate || 0) > 0, amount: l.amount || 0 }))
    if (invoice.credit_note_of_invoice_id) {
      const { data: orig } = await supabase.from('invoices').select('invoice_no').eq('id', invoice.credit_note_of_invoice_id).maybeSingle()
      creditedInvoiceNo = orig?.invoice_no ?? null
    }
  } else {
    const { data: charges } = await supabase
      .from('job_charges')
      .select('sale_amount, sale_vat_rate, description, notes, charge_code:charge_codes(description)')
      .eq('invoice_id', invoiceId)
    lines = (charges || []).map((c: any) => ({
      description: c.charge_code?.description || c.description || '(charge)',
      remarks: c.notes || null,
      taxable: (c.sale_vat_rate || 0) > 0,
      amount: c.sale_amount || 0,
    }))
  }
  const taxable_subtotal = lines.filter(l => l.taxable).reduce((s, l) => s + l.amount, 0)
  const non_taxable_subtotal = lines.filter(l => !l.taxable).reduce((s, l) => s + l.amount, 0)
  const net = taxable_subtotal + non_taxable_subtotal
  const vat_amount = invoice.vat_amount || 0
  const vat_rate_pct = taxable_subtotal > 0 ? Math.round((vat_amount / taxable_subtotal) * 10000) / 100 : 0
  const gross = invoice.total_amount ?? (net + vat_amount)

  let job_ref: string | null = null, your_reference: string | null = null
  let billing_lines_override: string[] | null = null
  let shipper_name: string | null = null, shipper_address_lines: string[] = []
  let consignee_name: string | null = null, consignee_address_lines: string[] = []
  let origin: string | null = null, destination: string | null = null, final_destination: string | null = null
  let arrival_date: string | null = null, master_no: string | null = null, vessel: string | null = null
  let containers: InvoicePDFContainer[] = []
  let pieces: number | null = null, cube: number | null = null
  let weight_chargeable: number | null = null, weight_actual: number | null = null

  if (invoice.job_id) {
    const { data: job } = await supabase
      .from('jobs')
      .select(`
        job_ref, client_ref, invoice_to_id, importer_id, consignor_id, hbl_importer_id, hbl_consignor_id, parent_job_id, job_type,
        job_sea_details(master_bl_no, house_bl_no, vessel_name, arrival_date, final_destination, departure_port:ports!departure_port_id(name), arrival_port:ports!arrival_port_id(name)),
        job_air_details(mawb_no, hawb_no, arrival_date, departure_port:ports!departure_airport_id(name), arrival_port:ports!arrival_airport_id(name))
      `)
      .eq('id', invoice.job_id)
      .maybeSingle()

    if (job) {
      job_ref = job.job_ref || null
      your_reference = job.client_ref || null
      const sea = Array.isArray((job as any).job_sea_details) ? (job as any).job_sea_details[0] : (job as any).job_sea_details
      const air = Array.isArray((job as any).job_air_details) ? (job as any).job_air_details[0] : (job as any).job_air_details
      // Shipper / Consignee = the same parties the declaration and the sync use (one shared rule): the house bill's on a
      // consol child or a one-master-one-house job, else the master's. It used to read the master's only.
      const flags = docFlagsOf(sea, air)
      const pickedConsignor = pickParty(job as any, 'consignor', flags)
      const pickedImporter = pickParty(job as any, 'importer', flags)
      const ids = [pickedConsignor.id, pickedImporter.id].filter((x): x is string => !!x)
      const { data: partyRows } = ids.length ? await supabase.from('customers').select(PARTY_SELECT).in('id', ids) : { data: [] }
      const partyById = new Map<string, any>(((partyRows as any[]) ?? []).map(r => [r.id as string, r]))
      const consignorRow = pickedConsignor.id ? partyById.get(pickedConsignor.id) ?? null : null
      const importerRow = pickedImporter.id ? partyById.get(pickedImporter.id) ?? null : null
      shipper_name = consignorRow?.name || null
      shipper_address_lines = (await jobPartyLines(invoice.job_id, pickedConsignor.role as JobPartyRole)) ?? addressLines(consignorRow)
      consignee_name = importerRow?.name || null
      consignee_address_lines = (await jobPartyLines(invoice.job_id, pickedImporter.role as JobPartyRole)) ?? addressLines(importerRow)
      // The invoice is billed to the job's Invoice To -> print the address recorded for that role on the job.
      if ((job as any).invoice_to_id && (job as any).invoice_to_id === invoice.customer_id) {
        const billing = await jobPartyLines(invoice.job_id, 'invoice_to')
        if (billing) billing_lines_override = billing
      }

      origin = sea?.departure_port?.name || air?.departure_port?.name || null
      destination = sea?.arrival_port?.name || air?.arrival_port?.name || null
      final_destination = sea?.final_destination || null
      arrival_date = sea?.arrival_date || air?.arrival_date || null
      master_no = sea?.master_bl_no || air?.mawb_no || null
      vessel = sea?.vessel_name || null
    }

    const { data: containerRows } = await supabase
      .from('job_containers')
      .select('container_no, seal_no, said_to_contain, container_type:container_types(code)')
      .eq('job_id', invoice.job_id)
    containers = (containerRows || []).map((c: any) => ({
      container_no: c.container_no || null,
      seal_no: c.seal_no || null,
      container_type: c.container_type?.code || null,
      said_to_contain: c.said_to_contain || null,
    }))

    const { data: goods } = await supabase
      .from('job_goods')
      .select('no_of_packs, gross_weight, volume_m3, chargeable_weight_hawb, chargeable_weight_mawb')
      .eq('job_id', invoice.job_id)
    if (goods && goods.length > 0) {
      pieces = goods.reduce((s, g) => s + (g.no_of_packs || 0), 0)
      cube = goods.reduce((s, g) => s + (g.volume_m3 || 0), 0)
      weight_actual = goods.reduce((s, g) => s + (g.gross_weight || 0), 0)
      weight_chargeable = goods.reduce((s, g) => s + (g.chargeable_weight_hawb ?? g.chargeable_weight_mawb ?? 0), 0)
    }
  }

  return {
    company,
    invoice_no: invoice.invoice_no,
    is_credit_note: isCreditNote,
    credit_note_of_invoice_no: creditedInvoiceNo,
    // Older rows have no category; a real Duty & VAT number always ends in D (K8766D), so that
    // is a safe fallback rather than printing them untyped.
    invoice_category: invoice.invoice_category || (/^K\d+D$/.test(invoice.invoice_no) ? 'DUTY_VAT' : null),
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date || null,
    currency: invoice.currency || 'GBP',
    vat_number: company.vat_number,
    prepared_by,
    customer_account_number: customer?.code || null,
    billing_name: customer?.name || null,
    billing_address_lines: billing_lines_override ?? addressLines(customer),
    job_ref,
    your_reference,
    shipper_name,
    shipper_address_lines,
    consignee_name,
    consignee_address_lines,
    origin,
    destination,
    final_destination,
    arrival_date,
    master_no,
    vessel,
    containers,
    pieces,
    cube,
    weight_chargeable,
    weight_actual,
    lines,
    invoice_notes: invoice.notes || null,
    taxable_subtotal,
    non_taxable_subtotal,
    net,
    vat_amount,
    vat_rate_pct,
    gross,
  }
}
